import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, MenuItem, Grid, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  CircularProgress, Chip, InputAdornment, IconButton, Divider, Tabs, Tab
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Search as SearchIcon, Close as CloseIcon, Download as DownloadIcon,
  Business as BusinessIcon, Phone as PhoneIcon, Email as EmailIcon,
  Star as StarIcon, StarBorder as StarBorderIcon, Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmtShort = (n) => {
  if (!n) return '₹0';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  return `₹${Number(n).toLocaleString('en-IN')}`;
};

const STATUS_COLORS = {
  active: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  inactive: { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' },
  blacklisted: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
};

const StarRating = ({ value, onChange }) => (
  <Box sx={{ display: 'flex', gap: 0.5 }}>
    {[1, 2, 3, 4, 5].map(i => (
      <IconButton key={i} size="small" onClick={() => onChange && onChange(i)} sx={{ p: 0.3 }}>
        {i <= (value || 0) ? <StarIcon sx={{ fontSize: 20, color: '#f59e0b' }} /> : <StarBorderIcon sx={{ fontSize: 20, color: '#d1d5db' }} />}
      </IconButton>
    ))}
  </Box>
);

const EMPTY_FORM = {
  vendorName: '', contactPerson: '', phone: '', email: '', address: '',
  gstNumber: '', panNumber: '', bankAccount: '', ifscCode: '', bankName: '',
  paymentTerms: '', rating: 0, status: 'active', notes: ''
};

export default function VendorsPage() {
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Selected vendor for detail view
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorDetail, setVendorDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState(0);

  // Add/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);

  // Import dialog
  const [importing, setImporting] = useState(false);

  useEffect(() => { fetchVendors(); }, []);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get(`${API_BASE_URL}/vendors`, { ...authHeaders(), params });
      setVendors(res.data.data.vendors || []);
    } catch (e) { setError('Failed to load vendors'); }
    finally { setLoading(false); }
  };

  const fetchVendorDetail = async (id) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/vendors/${id}`, authHeaders());
      setVendorDetail(res.data.data);
    } catch (e) { setError('Failed to load vendor details'); }
    finally { setDetailLoading(false); }
  };

  const handleSearch = (e) => { setSearch(e.target.value); };
  const handleSearchSubmit = (e) => { if (e.key === 'Enter') fetchVendors(); };

  const openAdd = () => { setEditingVendor(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (v) => { setEditingVendor(v); setForm({ ...EMPTY_FORM, ...v }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.vendorName.trim()) { setError('Vendor name is required'); return; }
    setSaving(true);
    try {
      if (editingVendor) {
        await axios.put(`${API_BASE_URL}/vendors/${editingVendor.id}`, form, authHeaders());
        setSuccess('Vendor updated successfully');
      } else {
        await axios.post(`${API_BASE_URL}/vendors`, form, authHeaders());
        setSuccess('Vendor created successfully');
      }
      setDialogOpen(false);
      fetchVendors();
      if (selectedVendor) fetchVendorDetail(selectedVendor.id);
    } catch (e) { setError(e.response?.data?.message || 'Failed to save vendor'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/vendors/${vendorToDelete.id}`, authHeaders());
      setSuccess('Vendor deleted');
      setDeleteDialogOpen(false);
      setVendorToDelete(null);
      if (selectedVendor?.id === vendorToDelete.id) setSelectedVendor(null);
      fetchVendors();
    } catch (e) { setError('Failed to delete vendor'); }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/vendors/import-from-comparison`, {}, authHeaders());
      setSuccess(res.data.message);
      fetchVendors();
    } catch (e) { setError('Import failed'); }
    finally { setImporting(false); }
  };

  const handleSelectVendor = (v) => {
    setSelectedVendor(v);
    setDetailTab(0);
    fetchVendorDetail(v.id);
  };

  const filteredVendors = vendors.filter(v => {
    const q = search.toLowerCase();
    return !q || v.vendorName?.toLowerCase().includes(q) || v.vendorCode?.toLowerCase().includes(q) || v.gstNumber?.toLowerCase().includes(q) || v.phone?.includes(q);
  }).filter(v => !statusFilter || v.status === statusFilter);

  return (
    <Box sx={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');`}</style>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: '1.7rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>Vendor Master</Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#64748b', mt: 0.3 }}>Central directory of all vendors and suppliers</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleImport} disabled={importing}
            sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 600, fontSize: '0.78rem' }}>
            {importing ? 'Importing...' : 'Import from Comparison'}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}
            sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 700, background: 'linear-gradient(135deg,#2563eb,#1d4ed8)' }}>
            Add Vendor
          </Button>
        </Box>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={2.5}>
        {/* Left: Vendor List */}
        <Grid item xs={12} md={selectedVendor ? 5 : 12}>
          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Search vendors..." value={search}
              onChange={handleSearch} onKeyDown={handleSearchSubmit}
              sx={{ flex: 1, minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: '10px', background: 'white' } }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
                endAdornment: search && <InputAdornment position="end"><IconButton size="small" onClick={() => { setSearch(''); fetchVendors(); }}><CloseIcon fontSize="small" /></IconButton></InputAdornment>
              }} />
            <TextField select size="small" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); }}
              displayEmpty sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: '10px', background: 'white' } }}>
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="blacklisted">Blacklisted</MenuItem>
            </TextField>
            <IconButton onClick={fetchVendors} sx={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px' }}><RefreshIcon /></IconButton>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : filteredVendors.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 10, background: 'white', borderRadius: '16px', border: '2px dashed #e2e8f0' }}>
              <BusinessIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
              <Typography sx={{ fontWeight: 700, color: '#64748b', mb: 1 }}>No vendors found</Typography>
              <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8', mb: 3 }}>Add your first vendor or import from Vendor Comparison</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}
                sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 700, background: 'linear-gradient(135deg,#2563eb,#1d4ed8)' }}>
                Add First Vendor
              </Button>
            </Box>
          ) : (
            <Paper sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {['Vendor', 'GST', 'Phone', 'Payment Terms', 'Total Spent', 'Status', ''].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#475569', background: '#f8fafc', py: 1.5 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredVendors.map((v, i) => {
                      const sc = STATUS_COLORS[v.status] || STATUS_COLORS.active;
                      const isSelected = selectedVendor?.id === v.id;
                      return (
                        <TableRow key={v.id} hover onClick={() => handleSelectVendor(v)}
                          sx={{ cursor: 'pointer', background: isSelected ? '#eff6ff' : i % 2 === 0 ? 'white' : '#fafafa', '&:hover': { background: '#eff6ff' } }}>
                          <TableCell>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>{v.vendorName}</Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{v.vendorCode} {v.contactPerson && `· ${v.contactPerson}`}</Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.78rem', color: '#64748b', fontFamily: 'monospace' }}>{v.gstNumber || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{v.phone || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{v.paymentTerms ? `${v.paymentTerms} days` : '-'}</TableCell>
                          <TableCell><Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#2563eb' }}>{fmtShort(v.totalSpent)}</Typography></TableCell>
                          <TableCell>
                            <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>
                              {v.status}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton size="small" onClick={e => { e.stopPropagation(); openEdit(v); }} sx={{ color: '#2563eb' }}><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" onClick={e => { e.stopPropagation(); setVendorToDelete(v); setDeleteDialogOpen(true); }} sx={{ color: '#dc2626' }}><DeleteIcon fontSize="small" /></IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ px: 3, py: 1.5, background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>{filteredVendors.length} vendor{filteredVendors.length !== 1 ? 's' : ''} · Total spent: {fmtShort(filteredVendors.reduce((s, v) => s + (v.totalSpent || 0), 0))}</Typography>
              </Box>
            </Paper>
          )}
        </Grid>

        {/* Right: Vendor Detail Panel */}
        {selectedVendor && (
          <Grid item xs={12} md={7}>
            <Paper sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              {/* Detail Header */}
              <Box sx={{ p: 3, background: 'linear-gradient(135deg,#0f172a,#1e293b)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: 'white' }}>{selectedVendor.vendorName}</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', mt: 0.3 }}>{selectedVendor.vendorCode}</Typography>
                  {selectedVendor.rating > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.3, mt: 0.5 }}>
                      {[1,2,3,4,5].map(i => <StarIcon key={i} sx={{ fontSize: 14, color: i <= selectedVendor.rating ? '#f59e0b' : 'rgba(255,255,255,0.2)' }} />)}
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(selectedVendor)}
                    sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', border: '1px solid', textTransform: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '0.75rem' }}>
                    Edit
                  </Button>
                  <IconButton size="small" onClick={() => setSelectedVendor(null)} sx={{ color: 'rgba(255,255,255,0.6)' }}><CloseIcon fontSize="small" /></IconButton>
                </Box>
              </Box>

              {/* Analytics Summary */}
              {vendorDetail && (
                <Box sx={{ display: 'flex', gap: 0, borderBottom: '1px solid #e2e8f0' }}>
                  {[
                    ['Total Spent', fmtShort(vendorDetail.analytics?.totalSpent), '#2563eb'],
                    ['Entries', vendorDetail.analytics?.entryCount || 0, '#16a34a'],
                    ['Sites', vendorDetail.analytics?.siteBreakdown?.length || 0, '#d97706'],
                  ].map(([label, value, color], i) => (
                    <Box key={label} sx={{ flex: 1, p: 2, textAlign: 'center', borderRight: i < 2 ? '1px solid #e2e8f0' : 'none' }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Typography>
                      <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color, fontFamily: 'Plus Jakarta Sans' }}>{value}</Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Tabs */}
              <Box sx={{ borderBottom: '1px solid #e2e8f0' }}>
                <Tabs value={detailTab} onChange={(e, v) => setDetailTab(v)}
                  sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.82rem' }, '& .Mui-selected': { color: '#2563eb' }, '& .MuiTabs-indicator': { backgroundColor: '#2563eb' } }}>
                  <Tab label="Details" />
                  <Tab label="Analytics" />
                  <Tab label="Recent Entries" />
                </Tabs>
              </Box>

              <Box sx={{ p: 3, maxHeight: 400, overflowY: 'auto' }}>
                {detailLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                ) : (
                  <>
                    {/* Tab 0: Details */}
                    {detailTab === 0 && vendorDetail && (
                      <Grid container spacing={2}>
                        {[
                          ['Contact Person', vendorDetail.vendor.contactPerson],
                          ['Phone', vendorDetail.vendor.phone],
                          ['Email', vendorDetail.vendor.email],
                          ['GST Number', vendorDetail.vendor.gstNumber],
                          ['PAN Number', vendorDetail.vendor.panNumber],
                          ['Payment Terms', vendorDetail.vendor.paymentTerms ? `${vendorDetail.vendor.paymentTerms} days credit` : null],
                          ['Bank Name', vendorDetail.vendor.bankName],
                          ['Account No.', vendorDetail.vendor.bankAccount],
                          ['IFSC Code', vendorDetail.vendor.ifscCode],
                          ['Status', vendorDetail.vendor.status],
                        ].filter(([, v]) => v).map(([label, value]) => (
                          <Grid item xs={6} key={label}>
                            <Box sx={{ p: 1.5, background: '#f8fafc', borderRadius: '10px' }}>
                              <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.3 }}>{label}</Typography>
                              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{value}</Typography>
                            </Box>
                          </Grid>
                        ))}
                        {vendorDetail.vendor.address && (
                          <Grid item xs={12}>
                            <Box sx={{ p: 1.5, background: '#f8fafc', borderRadius: '10px' }}>
                              <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.3 }}>Address</Typography>
                              <Typography sx={{ fontSize: '0.85rem', color: '#1e293b' }}>{vendorDetail.vendor.address}</Typography>
                            </Box>
                          </Grid>
                        )}
                        {vendorDetail.vendor.notes && (
                          <Grid item xs={12}>
                            <Box sx={{ p: 1.5, background: '#fffbeb', borderRadius: '10px', border: '1px solid #fde68a' }}>
                              <Typography sx={{ fontSize: '0.68rem', color: '#d97706', fontWeight: 600, mb: 0.3 }}>NOTES</Typography>
                              <Typography sx={{ fontSize: '0.82rem', color: '#92400e' }}>{vendorDetail.vendor.notes}</Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    )}

                    {/* Tab 1: Analytics */}
                    {detailTab === 1 && vendorDetail && (
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a', mb: 2 }}>Spending by Site</Typography>
                        {vendorDetail.analytics?.siteBreakdown?.length === 0 ? (
                          <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>No spending data yet</Typography>
                        ) : (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                {['Site', 'Location', 'Entries', 'Total Spent'].map(h => (
                                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.75rem', background: '#f8fafc' }}>{h}</TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {vendorDetail.analytics?.siteBreakdown?.map((s, i) => (
                                <TableRow key={i} hover>
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{s.siteName}</TableCell>
                                  <TableCell sx={{ fontSize: '0.78rem', color: '#64748b' }}>{s.location}</TableCell>
                                  <TableCell sx={{ fontSize: '0.78rem' }}>{s.entryCount}</TableCell>
                                  <TableCell sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#2563eb' }}>{fmtShort(s.totalSpent)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </Box>
                    )}

                    {/* Tab 2: Recent Entries */}
                    {detailTab === 2 && vendorDetail && (
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a', mb: 2 }}>Recent Material Entries</Typography>
                        {vendorDetail.analytics?.recentEntries?.length === 0 ? (
                          <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>No entries found</Typography>
                        ) : (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                {['Date', 'Material', 'Site', 'Bill No', 'Amount'].map(h => (
                                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.75rem', background: '#f8fafc' }}>{h}</TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {vendorDetail.analytics?.recentEntries?.map((e, i) => (
                                <TableRow key={i} hover>
                                  <TableCell sx={{ fontSize: '0.75rem' }}>{e.date ? new Date(e.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</TableCell>
                                  <TableCell sx={{ fontSize: '0.78rem', maxWidth: 120 }}>
                                    <Typography sx={{ fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110 }}>{e.material || '-'}</Typography>
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem', color: '#64748b' }}>{e.siteName || '-'}</TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem', color: '#64748b' }}>{e.billNumber || '-'}</TableCell>
                                  <TableCell sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#2563eb' }}>₹{Number(e.amount || 0).toLocaleString('en-IN')}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 800, fontFamily: 'Plus Jakarta Sans, sans-serif', borderBottom: '1px solid #e2e8f0' }}>
          {editingVendor ? '✏️ Edit Vendor' : '➕ Add New Vendor'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            {/* Basic Info */}
            <Grid item xs={12}><Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>Basic Information</Typography></Grid>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth label="Vendor Name *" value={form.vendorName} onChange={e => setForm(p => ({ ...p, vendorName: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label="Status" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="blacklisted">Blacklisted</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Contact Person" value={form.contactPerson} onChange={e => setForm(p => ({ ...p, contactPerson: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Phone" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Payment Terms (days)" type="number" value={form.paymentTerms} onChange={e => setForm(p => ({ ...p, paymentTerms: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Address" multiline rows={2} value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            </Grid>

            {/* Tax Info */}
            <Grid item xs={12}><Divider /><Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', mt: 2, mb: 0.5 }}>Tax Information</Typography></Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="GST Number" value={form.gstNumber} onChange={e => setForm(p => ({ ...p, gstNumber: e.target.value.toUpperCase() }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: 'monospace' } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="PAN Number" value={form.panNumber} onChange={e => setForm(p => ({ ...p, panNumber: e.target.value.toUpperCase() }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: 'monospace' } }} />
            </Grid>

            {/* Bank Info */}
            <Grid item xs={12}><Divider /><Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', mt: 2, mb: 0.5 }}>Bank Details</Typography></Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Bank Name" value={form.bankName} onChange={e => setForm(p => ({ ...p, bankName: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Account Number" value={form.bankAccount} onChange={e => setForm(p => ({ ...p, bankAccount: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="IFSC Code" value={form.ifscCode} onChange={e => setForm(p => ({ ...p, ifscCode: e.target.value.toUpperCase() }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: 'monospace' } }} />
            </Grid>

            {/* Rating & Notes */}
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600, mb: 0.5 }}>Rating</Typography>
              <StarRating value={form.rating} onChange={v => setForm(p => ({ ...p, rating: v }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notes" multiline rows={2} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, borderTop: '1px solid #e2e8f0', pt: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none', borderRadius: '9px' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ textTransform: 'none', borderRadius: '9px', fontWeight: 700, background: 'linear-gradient(135deg,#2563eb,#1d4ed8)' }}>
            {saving ? 'Saving...' : editingVendor ? 'Update Vendor' : 'Create Vendor'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Vendor</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ borderRadius: '10px' }}>
            Are you sure you want to delete <strong>{vendorToDelete?.vendorName}</strong>? This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: 'none', borderRadius: '9px' }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} sx={{ textTransform: 'none', borderRadius: '9px', fontWeight: 700 }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
